/**
 * Zustand und Ablauf des Wizards „Neue Kampagne“.
 *
 * Der Wizard-Zustand liegt lokal und wird (entprellt) in den Entwurf der Kampagne geschrieben.
 * Beim Verlassen eines Schritts wird sofort gespeichert; Schritt 3 → 4 startet die Suche,
 * damit die Kandidatenliste echte Kandidaten zeigt.
 */

import { Routes } from 'enums';
import i18n from 'i18n';
import { useLinkedinAccountsQuery } from 'features/co-pilot/linkedin-accounts/hooks/use-linkedin-accounts-query';
import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { useLocation, useNavigate, useParams, useSearchParams } from 'react-router';
import {
  checkBriefing,
  createWizardCampaign,
  deriveBriefing,
  estimateSearch,
  getWizard,
  planFlow,
  saveWizardState,
  startSearch,
  startWizardCampaign,
  WizardApiError,
} from './campaign-wizard.api';
import {
  accountDisplayName,
  applyDerivation,
  needsDerivation,
  clampStep,
  derivationKey,
  flowAnswersFromState,
  isLinkedinUrl,
  searchLinkRows,
  stepBlocker,
  accountWorkWindowMissing,
  withWizardDefaults,
} from './campaign-wizard.logic';
import {
  adSignatureSource,
  allowedStep,
  emptyCheckResult,
  GATE_RED_THRESHOLD,
  gateDecision,
  hasPlaceholders,
  isCheckRequiredError,
  isLocationRequiredError,
  locationMissing,
  locationQuestionVisible,
  withSignature,
} from './steps/job/job-gate.logic';
import {
  AdCheckResult,
  FlowPlanResult,
  RunSnapshot,
  SearchLinkCheck,
  WIZARD_DEFAULTS,
  WizardAccount,
  WizardAutoReplyDefaults,
  WizardSnapshot,
  WizardState,
  WizardStep,
} from './campaign-wizard.types';
import {
  duplicateName,
  isSearchConfirmed,
  needsSearchRestartConfirm,
  searchAlreadyRan,
  type SearchConfirmation,
} from './search-gate.logic';
import { startFailureOf } from './steps/candidate-list/candidate-list.logic';
import { useUserData } from 'hooks/use-user-data';
import { applyWizardPrefill, readWizardPrefill } from './wizard-prefill';
import {
  applyStep1Buffer,
  clearStep1Buffer,
  readStep1Buffer,
  writeStep1Buffer,
} from './wizard-step1-buffer';

export const NEW_CAMPAIGN_ROUTE_SEGMENT = 'new-campaign';

export interface EstimateState {
  value: number | null;
  status: 'idle' | 'loading' | 'ok' | 'unavailable';
  message?: string;
  /** Nur bei eigenem Suchlink. */
  link?: SearchLinkCheck;
}

/** Prüfpflicht in Schritt 1 (Anzeigenprüfung und Lücken-Popup). */
export interface AdGate {
  /** `checking`: Prüfung läuft; `warn`: Popup „Anzeige ergänzen“ ist offen. */
  status: 'idle' | 'checking' | 'warn';
  /** Die Prüfung selbst ist fehlgeschlagen (Netzwerk/Backend). */
  checkFailed: boolean;
  /** Wie oft die Prüfung seit dem letzten Erfolg fehlgeschlagen ist (ab 2: „Trotzdem weiter“). */
  checkFailures: number;
  /** Fehlende Punkte im Prüfbereich hervorheben (nach „Anzeige ergänzen“). */
  highlight: boolean;
  /** Zählt hoch, wenn das Anzeigenfeld den Fokus bekommen soll. */
  focusTick: number;
  /** Zählt hoch, wenn die Pflichtfrage nach dem Ort Fokus bekommen soll. */
  locationTick: number;
  /** Ziel, zu dem nach „Trotzdem weiter“ gesprungen wird. */
  pendingTarget: WizardStep | null;
}

export interface WizardController {
  campaignId: string | null;
  loading: boolean;
  loadError: string | null;
  /** Kampagne ist kein Entwurf mehr. */
  locked: boolean;
  state: WizardState;
  patch: (partial: Partial<WizardState>) => void;
  step: WizardStep;
  goTo: (step: WizardStep) => void;
  gate: AdGate;
  /**
   * Anzeige mit KI prüfen (gemeinsam für Button und „Weiter“). `text` und `overrides` gelten, wenn der Zustand
   * noch nicht neu gerendert wurde (z. B. direkt nach dem Einlesen eines Links). Liefert das Ergebnis, bei Fehler `null`.
   */
  runAdCheck: (
    text?: string,
    overrides?: Partial<Pick<WizardState, 'briefingExtras'>>
  ) => Promise<AdCheckResult | null>;
  /** Popup: „Überspringen, ohne weitere Optimierung“. */
  gateProceed: () => void;
  /** Prüfung ist mehrfach fehlgeschlagen: ohne Prüfergebnis weiter. */
  gateSkipCheck: () => void;
  /** Popup: „Anzeige ergänzen“. */
  gateAmend: () => void;
  next: () => Promise<void>;
  prev: () => void;
  cancel: () => void;
  /** Umschalter „Erweiterte Einrichtung“: zur alten Ansicht (Builder bzw. alter Erstellen-Dialog). */
  openLegacySetup: () => Promise<void>;
  saveDraft: () => Promise<void>;
  start: () => Promise<void>;
  /** Warum „Weiter“ gerade nicht geht (Schlüssel aus `stepBlocker`) – null = darf weiter. */
  blocker: string | null;
  /** Der Nutzer hat „Weiter“ gedrückt, obwohl etwas fehlt: Fehler an den Feldern zeigen. */
  showBlocker: boolean;
  busy: boolean;
  saving: boolean;
  saveError: string | null;
  /** Hinweis ohne Fehlerpräfix (z. B. „Bitte prüfe zuerst die Anzeige.“). */
  notice: string | null;
  nameTaken: boolean;
  startError: string | null;
  /** Fester Fehlercode des Servers zum Startfehler (z. B. ACCOUNT_WORK_WINDOW_MISSING). */
  startErrorCode: string | null;
  warnings: string[];
  /** Der Ablauf konnte nicht in die Kampagne geschrieben werden (Klartext vom Backend). */
  flowError: string | null;
  /** Meldung von `search/start` (z. B. LinkedIn nicht erreichbar). */
  searchMessage: string | null;
  /** Grund, wenn die Suche nicht angelegt werden konnte (z. B. LinkedIn-Sitzung abgelaufen); null = kein Fehler bekannt. */
  searchFailure: string | null;
  setSearchFailure: (failure: string | null) => void;
  accounts: WizardAccount[];
  accountsLoading: boolean;
  account: WizardAccount | null;
  estimate: EstimateState;
  /** Prüfung je eigenem Link (Schritt 2), Schlüssel = Link ohne Leerzeichen am Rand. */
  linkChecks: Record<string, EstimateState>;
  flowPlan: FlowPlanResult | null;
  flowPlanLoading: boolean;
  /** Die Berechnung des Ablaufs ist fehlgeschlagen und es gibt keinen früheren Stand (erst dann der Fehlertext, vorher „wird berechnet“). */
  flowPlanFailed: boolean;
  run: RunSnapshot | null;
  /** Anzeige der Laufansicht („Kampagne gestartet“). */
  running: boolean;
  /** Vorgaben der automatischen Antworten je Sprache (aus dem Snapshot); null, solange nichts geladen ist. */
  autoReplyDefaults: WizardAutoReplyDefaults | null;
  /** Schreibt den aktuellen Stand sofort (z. B. vor dem Test der automatischen Antwort, der den gespeicherten Stand nutzt). */
  flushSave: () => Promise<void>;
  /** Erneut laden (z. B. nach Klonen). */
  reload: () => Promise<void>;
  /** Übernimmt einen vom Backend gelieferten Zustand (z. B. nach dem Klonen). */
  adoptCampaign: (campaignId: string, state: WizardState) => void;
  /**
   * Läuft die Kampagne schon: legt eine Kopie (ohne Kontakte) mit den Daten dieser Kampagne an und öffnet sie im Assistenten.
   * Bei vergebenem Namen wird eine Zahl angehängt. Wirft bei Fehlern den `WizardApiError`.
   */
  duplicateCampaign: () => Promise<void>;
  /** Eine Änderung der Suchkriterien wartet auf die Bestätigung „Neue Suche starten“. */
  searchRestartPending: boolean;
  confirmSearchRestart: () => void;
  cancelSearchRestart: () => void;
  /** Für diesen Entwurf wurde schon gesucht (Schritt 2 weist darauf hin). */
  searchRan: boolean;
  /** Die Kandidatenliste meldet eine Suche (z. B. nach „Suche erneut starten“): ab jetzt vor Änderungen der Suche fragen. */
  markSearchExists: () => void;
}

const toAccountOption = (a: any): WizardAccount => ({
  id: a._id,
  name: accountDisplayName(a),
  email: a.userEmail ?? '',
  productType:
    a.liProductType === 'recruiter'
      ? 'recruiter'
      : a.liProductType === 'sales_navigator'
        ? 'sales_navigator'
        : 'normal',
  workDays: Array.isArray(a.workDays) ? a.workDays : undefined,
  workHours: Array.isArray(a.workHours) ? a.workHours : undefined,
});

const SAVE_DEBOUNCE_MS = 800;

export const useCampaignWizard = (): WizardController => {
  const { campaignId: campaignParam } = useParams();
  const [searchParams, setSearchParams] = useSearchParams();
  const navigate = useNavigate();
  const location = useLocation();

  const { data: accountList, isLoading: accountsLoading } = useLinkedinAccountsQuery({
    prioritizeCurrentUser: true,
  });
  const accounts = useMemo(() => (accountList ?? []).map(toAccountOption), [accountList]);

  const [campaignId, setCampaignId] = useState<string | null>(campaignParam ?? null);
  const [state, setState] = useState<WizardState>({ ...WIZARD_DEFAULTS, accountId: '' });
  const [snapshot, setSnapshot] = useState<WizardSnapshot | null>(null);
  const [loading, setLoading] = useState<boolean>(!!campaignParam);
  const [loadError, setLoadError] = useState<string | null>(null);
  const [locked, setLocked] = useState(false);
  // Gibt es schon eine Suche mit Kandidaten? undefined = noch nicht vom Server geladen (dann wird lieber gefragt).
  const [hasSearch, setHasSearchState] = useState<boolean | undefined>(undefined);
  const hasSearchRef = useRef<boolean | undefined>(undefined);
  const setHasSearch = useCallback((value: boolean | undefined) => {
    hasSearchRef.current = value;
    setHasSearchState(value);
  }, []);
  const [busy, setBusy] = useState(false);
  const [saving, setSaving] = useState(false);
  const [saveError, setSaveError] = useState<string | null>(null);
  const [notice, setNotice] = useState<string | null>(null);
  const [nameTaken, setNameTaken] = useState(false);
  const [startError, setStartError] = useState<string | null>(null);
  const [startErrorCode, setStartErrorCode] = useState<string | null>(null);
  const [warnings, setWarnings] = useState<string[]>([]);
  const [flowError, setFlowError] = useState<string | null>(null);
  const [searchMessage, setSearchMessage] = useState<string | null>(null);
  const [searchFailure, setSearchFailure] = useState<string | null>(null);
  const [showBlocker, setShowBlocker] = useState(false);
  const [estimate, setEstimate] = useState<EstimateState>({ value: null, status: 'idle' });
  const [flowPlan, setFlowPlan] = useState<FlowPlanResult | null>(null);
  const [flowPlanLoading, setFlowPlanLoading] = useState(false);
  const [flowPlanFailed, setFlowPlanFailed] = useState(false);
  const [run, setRun] = useState<RunSnapshot | null>(null);
  const [gate, setGate] = useState<AdGate>({
    status: 'idle',
    checkFailed: false,
    checkFailures: 0,
    highlight: false,
    focusTick: 0,
    locationTick: 0,
    pendingTarget: null,
  });

  /** Der Ort fehlt: nicht weiter, die Ortsfrage im Prüfergebnis zeigen (nicht überspringbar). */
  const askLocation = useCallback(() => {
    setNotice(i18n.t('wizGateLocationRequired'));
    setGate(g => ({ ...g, status: 'idle', pendingTarget: null, highlight: true, locationTick: g.locationTick + 1 }));
  }, []);

  const stateRef = useRef(state);
  stateRef.current = state;
  const campaignIdRef = useRef(campaignId);
  campaignIdRef.current = campaignId;
  const dirtyRef = useRef(false);
  // Änderung der Suchkriterien nach gelaufener Suche: Patch zurückhalten, bis der Nutzer bestätigt (einmal je Bearbeitung, also je Schritt-Aufenthalt).
  const [searchRestartPending, setSearchRestartPending] = useState(false);
  const pendingPatchRef = useRef<Partial<WizardState> | null>(null);
  // Gilt nur für die aktuelle Bearbeitung: wechselt der Schritt, ist sie weg (siehe Effekt unten).
  const searchConfirmed = useRef<SearchConfirmation | null>(null);
  const saveTimer = useRef<ReturnType<typeof setTimeout> | null>(null);
  const savePromise = useRef<Promise<void>>(Promise.resolve());

  const step = clampStep(Number(searchParams.get('step') ?? state.step ?? 1));
  const running = searchParams.get('run') === '1';
  const stepRef = useRef(step);
  stepRef.current = step;

  // Die Bestätigung des Suchneustarts gilt nur für die aktuelle Bearbeitung: beim Schrittwechsel verfällt sie.
  useEffect(() => {
    searchConfirmed.current = null;
  }, [step]);

  /* Laden ------------------------------------------------------------ */

  const applySnapshot = useCallback((snap: WizardSnapshot) => {
    setSnapshot(snap);
    if (snap.hasSearch !== undefined) setHasSearch(snap.hasSearch);
    // Der Server führt den höchsten erreichten Schritt (`maxStep`); der Client übernimmt ihn.
    if (snap.state.maxStep !== undefined)
      setState(prev =>
        prev.maxStep === snap.state.maxStep ? prev : { ...prev, maxStep: snap.state.maxStep }
      );
    setWarnings(snap.warnings ?? []);
    setFlowError(snap.flowError ?? null);
    setEstimate(prev =>
      snap.searchEstimate !== null && snap.searchEstimate !== undefined
        ? { value: snap.searchEstimate, status: 'ok' }
        : prev.status === 'loading'
          ? prev
          : prev
    );
  }, [setHasSearch]);

  const load = useCallback(
    async (id: string) => {
      setLoading(true);
      setLoadError(null);
      try {
        const snap = await getWizard(id);
        setState(withWizardDefaults(snap.state));
        setLocked(snap.status !== 'draft');
        applySnapshot(snap);
      } catch (e) {
        setLoadError((e as WizardApiError).message);
      } finally {
        setLoading(false);
      }
    },
    [applySnapshot]
  );

  useEffect(() => {
    if (campaignParam) {
      setCampaignId(campaignParam);
      void load(campaignParam);
    }
  }, [campaignParam]);

  // Start aus dem KI-Chat: Name, Konto, Anzeige aus dem Router-State in Schritt 1 vorbelegen (einmalig, nur leere Felder).
  useEffect(() => {
    if (campaignParam) return;
    const prefill = readWizardPrefill(location.state);
    if (prefill) setState(prev => applyWizardPrefill(prev, prefill));
  }, []);

  // Schritt-1-Eingaben vor dem ersten Speichern: nach F5 wiederherstellen (nach der Chat-Vorbelegung, nur leere Felder).
  const userId = useUserData().id;
  const bufferRestored = useRef(false);
  useEffect(() => {
    if (campaignParam || bufferRestored.current) return;
    bufferRestored.current = true;
    const buffer = readStep1Buffer(userId);
    if (buffer) setState(prev => applyStep1Buffer(prev, buffer));
  }, []);
  useEffect(() => {
    if (campaignParam || campaignId || !bufferRestored.current) return;
    writeStep1Buffer(userId, state);
  }, [state.briefing, state.briefingExtras, state.name, state.jobTitle, state.accountId, state.adCheck, campaignId]);

  // Frische Kampagne: erstes Konto vorauswählen, sobald die Konten da sind.
  useEffect(() => {
    if (!campaignIdRef.current && !stateRef.current.accountId && accounts.length > 0) {
      setState(prev => (prev.accountId ? prev : { ...prev, accountId: accounts[0].id }));
    }
  }, [accounts]);

  const account: WizardAccount | null = useMemo(() => {
    const fromList = accounts.find(a => a.id === state.accountId);
    if (fromList) return fromList;
    return snapshot?.account ?? null;
  }, [accounts, state.accountId, snapshot]);

  /* Speichern -------------------------------------------------------- */

  const writeState = useCallback(async (): Promise<void> => {
    const id = campaignIdRef.current;
    if (!id) return;
    const current = stateRef.current;
    dirtyRef.current = false;
    setSaving(true);
    try {
      // Gesendet wird der angezeigte Schritt: Beim Tippen in Schritt 1 ist der Autosave damit nie ein „Vorrücken“,
      // das der Server (Prüfpflicht) ablehnen müsste.
      const snap = await saveWizardState(id, current, stepRef.current);
      setSaveError(null);
      setNameTaken(false);
      applySnapshot(snap);
    } catch (e) {
      dirtyRef.current = true;
      const err = e as WizardApiError;
      if (err.nameTaken) setNameTaken(true);
      if (isCheckRequiredError(err)) {
        // Der Server verlangt die Anzeigenprüfung (z. B. Sprung per URL): zurück auf Schritt 1.
        setNotice(i18n.t('wizGateCheckRequired'));
        setSearchParams({ step: '1' }, { replace: true });
      } else if (isLocationRequiredError(err)) {
        setSearchParams({ step: '1' }, { replace: true });
        askLocation();
      } else {
        setSaveError(err.message);
      }
      throw err;
    } finally {
      setSaving(false);
    }
  }, [applySnapshot, setSearchParams, askLocation]);

  const flushSave = useCallback(async (): Promise<void> => {
    if (saveTimer.current) {
      clearTimeout(saveTimer.current);
      saveTimer.current = null;
    }
    // Läuft noch ein Speichern, danach noch einmal mit dem neuesten Stand.
    savePromise.current = savePromise.current
      .catch(() => undefined)
      .then(() => (campaignIdRef.current ? writeState() : undefined));
    await savePromise.current;
  }, [writeState]);

  const scheduleSave = useCallback(() => {
    if (!campaignIdRef.current) return;
    dirtyRef.current = true;
    if (saveTimer.current) clearTimeout(saveTimer.current);
    saveTimer.current = setTimeout(() => {
      saveTimer.current = null;
      void flushSave().catch(() => undefined);
    }, SAVE_DEBOUNCE_MS);
  }, [flushSave]);

  useEffect(
    () => () => {
      if (saveTimer.current) {
        clearTimeout(saveTimer.current);
        if (dirtyRef.current && campaignIdRef.current) void writeState().catch(() => undefined);
      }
    },

    []
  );

  const patch = useCallback(
    (incoming: Partial<WizardState>) => {
      // Anzeige und Ergänzungen werden nur in Schritt 1 bearbeitet; spätere Schritte senden nie
      // einen geänderten Text (sonst wäre die Prüfung ungültig).
      let partial = incoming;
      if (
        stepRef.current >= 2 &&
        (incoming.briefing !== undefined || incoming.briefingExtras !== undefined)
      ) {
        partial = { ...incoming };
        delete partial.briefing;
        delete partial.briefingExtras;
      }
      if (
        needsSearchRestartConfirm(
          stateRef.current,
          campaignIdRef.current,
          hasSearchRef.current,
          partial,
          isSearchConfirmed(searchConfirmed.current, campaignIdRef.current, stepRef.current)
        )
      ) {
        pendingPatchRef.current = { ...pendingPatchRef.current, ...partial };
        setSearchRestartPending(true);
        return;
      }
      setState(prev => ({ ...prev, ...partial }));
      setShowBlocker(false);
      scheduleSave();
    },
    [scheduleSave]
  );

  const confirmSearchRestart = useCallback(() => {
    const pending = pendingPatchRef.current;
    pendingPatchRef.current = null;
    if (campaignIdRef.current)
      searchConfirmed.current = { campaignId: campaignIdRef.current, step: stepRef.current };
    // Nach bestätigtem Neustart gibt es (bald) wieder eine Suche.
    hasSearchRef.current = true;
    setHasSearch(true);
    setSearchRestartPending(false);
    if (pending) patch(pending);
  }, [patch]);

  const markSearchExists = useCallback(() => {
    if (hasSearchRef.current !== true) setHasSearch(true);
  }, [setHasSearch]);

  const cancelSearchRestart = useCallback(() => {
    pendingPatchRef.current = null;
    setSearchRestartPending(false);
  }, []);

  /* Anzeigenprüfung (Schritt 1) -------------------------------------- */

  const runAdCheck = useCallback(
    async (text?: string, overrides: Partial<WizardState> = {}) => {
      const current = stateRef.current;
      const parts = {
        briefing: text ?? current.briefing,
        briefingExtras: overrides.briefingExtras ?? current.briefingExtras,
      };
      setNotice(null);
      setGate(g => ({ ...g, status: 'checking', checkFailed: false, highlight: false }));
      try {
        const res = await checkBriefing(parts.briefing, {
          extras: parts.briefingExtras,
        });
        patch({ adCheck: withSignature(res, adSignatureSource(parts)) });
        setGate(g => ({ ...g, status: 'idle', checkFailures: 0 }));
        return res;
      } catch {
        setGate(g => ({
          ...g,
          status: 'idle',
          checkFailed: true,
          checkFailures: g.checkFailures + 1,
        }));
        return null;
      }
    },
    [patch]
  );

  /* Navigation ------------------------------------------------------- */

  const setStepParam = useCallback(
    (target: WizardStep, id?: string | null, replace = false) => {
      const targetId = id ?? campaignIdRef.current;
      if (targetId && !campaignParam) {
        navigate(
          {
            pathname: `${Routes.RECRUITING_BOOSTER_CO_PILOT}/${NEW_CAMPAIGN_ROUTE_SEGMENT}/${targetId}`,
            search: `?step=${target}`,
          },
          { replace: true }
        );
        return;
      }
      setSearchParams({ step: String(target) }, { replace });
    },
    [campaignParam, navigate, setSearchParams]
  );

  const advanceRef = useRef<(jumpTo?: WizardStep, force?: boolean) => Promise<void>>(
    async () => undefined
  );

  const goTo = useCallback(
    (target: WizardStep) => {
      setShowBlocker(false);
      setStartError(null);
      if (step === 1 && target > 1) {
        // Sprung nach vorn aus Schritt 1: dieselbe Prüfung wie „Weiter“ (Prüfpflicht, ggf. Entwurf anlegen).
        void advanceRef.current(target);
        return;
      }
      if (!campaignIdRef.current) return;
      void flushSave().catch(() => undefined);
      setStepParam(target);
    },
    [flushSave, setStepParam, step]
  );

  const prev = useCallback(() => {
    if (step === 1) {
      navigate(Routes.RECRUITING_BOOSTER_CO_PILOT_PROJECTS);
      return;
    }
    goTo(clampStep(step - 1));
  }, [goTo, navigate, step]);

  const cancel = useCallback(() => {
    navigate(Routes.RECRUITING_BOOSTER_CO_PILOT_PROJECTS);
  }, [navigate]);

  // Mit Entwurf: diesen im alten Builder öffnen (vorher speichern). Ohne Entwurf: alter Dialog „Kampagne erstellen“.
  const openLegacySetup = useCallback(async () => {
    const id = campaignIdRef.current;
    if (id) {
      await flushSave().catch(() => undefined);
      navigate({ pathname: Routes.RECRUITING_BOOSTER_CO_PILOT_CAMPAIGN, search: `?_id=${id}` });
      return;
    }
    navigate(Routes.RECRUITING_BOOSTER_CO_PILOT_PROJECTS, { state: { openLegacyCreateDialog: true } });
  }, [flushSave, navigate]);

  const blocker = stepBlocker(step, state, {
    hasAccount: accounts.length > 0 || !!account,
    productType: account?.productType,
    workWindowMissing: accountWorkWindowMissing(account),
  });

  const saveDraft = useCallback(async () => {
    setBusy(true);
    try {
      await flushSave();
      navigate(Routes.RECRUITING_BOOSTER_CO_PILOT_PROJECTS);
    } catch {
      // saveError ist gesetzt und wird in der Fußleiste gezeigt
    } finally {
      setBusy(false);
    }
  }, [flushSave, navigate]);

  const start = useCallback(async () => {
    const id = campaignIdRef.current;
    if (!id) return;
    setBusy(true);
    setStartError(null);
    setStartErrorCode(null);
    try {
      setState(p => ({ ...p, step: 7 }));
      stateRef.current = { ...stateRef.current, step: 7 };
      await flushSave();
      const snap = await startWizardCampaign(id);
      setRun(snap);
      setSearchParams({ run: '1' }, { replace: true });
    } catch (e) {
      setStartError((e as WizardApiError).message);
      setStartErrorCode((e as WizardApiError).code ?? null);
    } finally {
      setBusy(false);
    }
  }, [flushSave, setSearchParams, askLocation]);

  const advance = useCallback(
    async (jumpTo?: WizardStep, force = false) => {
      if (step === 7) {
        // Pflichtauswahl (Freigabe, Bereits Kontaktierte) und Konto-Arbeitszeiten: erst klären, dann starten.
        if (blocker) {
          setShowBlocker(true);
          return;
        }
        await start();
        return;
      }
      if (blocker) {
        setShowBlocker(true);
        return;
      }
      // Pflichtangabe Ort: gilt immer, auch bei „Überspringen“ (force).
      if (step === 1 && gateDecision(stateRef.current.adCheck, stateRef.current) === 'location') {
        askLocation();
        return;
      }
      if (step === 1 && !force) {
        const { adCheck } = stateRef.current;
        const decision = gateDecision(adCheck, stateRef.current);
        if (decision === 'check') {
          // Prüfpflicht: erst prüfen und das Ergebnis zeigen; weiter geht es mit dem nächsten Klick.
          setBusy(true);
          try {
            const res = await runAdCheck(stateRef.current.briefing);
            if (res && locationMissing({ ...res, checkedText: '' })) {
              askLocation();
              return;
            }
            // Frisch geprüft und ≥ 1 offener Punkte: Popup gleich zeigen (mit „Überspringen“), nicht erst beim übernächsten Klick.
            if (
              res &&
              (res.items.filter(i => !i.ok).length >= GATE_RED_THRESHOLD ||
                hasPlaceholders(stateRef.current.briefingExtras))
            ) {
              setGate(g => ({ ...g, status: 'warn', pendingTarget: jumpTo ?? null }));
            }
          } finally {
            setBusy(false);
          }
          return;
        }
        if (decision === 'warn') {
          setGate(g => ({ ...g, status: 'warn', pendingTarget: jumpTo ?? null }));
          return;
        }
      }
      setBusy(true);
      setSaveError(null);
      try {
        const target = jumpTo ?? clampStep(step + 1);
        const advanced: WizardState = {
          ...stateRef.current,
          step: Math.max(stateRef.current.step, target) as WizardStep,
          maxStep: Math.max(
            stateRef.current.maxStep ?? stateRef.current.step,
            target
          ) as WizardStep,
        };
        if (step === 1 && !campaignIdRef.current) {
          const created = await createWizardCampaign({
            name: advanced.name.trim(),
            accountId: advanced.accountId,
            briefing: advanced.briefing,
            briefingExtras: advanced.briefingExtras,
          });
          // Das Backend liefert die abgeleiteten Vorgaben (Orte, Kriterien, Listen, Vorlage); Eingaben des Nutzers bleiben.
          const merged: WizardState = {
            ...created.state,
            briefing: advanced.briefing,
            briefingExtras: advanced.briefingExtras,
            name: advanced.name.trim(),
            accountId: advanced.accountId,
            adCheck: advanced.adCheck,
            // Die Vorgaben des Servers stammen aus genau diesem Text.
            derivedKey: derivationKey(advanced),
            step: jumpTo ?? 2,
          };
          setCampaignId(created.campaignId);
          campaignIdRef.current = created.campaignId;
          setHasSearch(false);
          clearStep1Buffer(userId);
          setState(merged);
          stateRef.current = merged;
          await flushSave();
          navigate(
            {
              pathname: `${Routes.RECRUITING_BOOSTER_CO_PILOT}/${NEW_CAMPAIGN_ROUTE_SEGMENT}/${created.campaignId}`,
              search: `?step=${jumpTo ?? 2}`,
            },
            { replace: true }
          );
          return;
        }
        // D-12: Klon ohne Briefing – nach der Prüfung die Ableitung nachholen (nur leere Felder).
        if (step === 1 && needsDerivation(advanced)) {
          try {
            const derived = await deriveBriefing(advanced.briefing, {
              extras: advanced.briefingExtras,
            });
            Object.assign(advanced, applyDerivation(advanced, derived));
          } catch {
            // Ohne Ableitung bleibt Schritt 2 leer; der Nutzer kann Orte selbst anlegen.
          }
        }
        setState(advanced);
        stateRef.current = advanced;
        await flushSave();
        if (step === 3 && campaignIdRef.current) {
          try {
            const res = await startSearch(campaignIdRef.current);
            setSearchMessage(res.message ?? null);
            setSearchFailure(startFailureOf(res));
            // Ab jetzt verwirft eine Änderung der Suchkriterien Kandidaten: Rückfrage, auch vor dem nächsten Autosave.
            if (res.started) setHasSearch(true);
          } catch (e) {
            setSearchMessage((e as WizardApiError).message);
          }
        }
        setNotice(null);
        setStepParam(target);
      } catch (e) {
        const err = e as WizardApiError;
        if (err.nameTaken) setNameTaken(true);
        if (isCheckRequiredError(err)) {
          setNotice(i18n.t('wizGateCheckRequired'));
          setSearchParams({ step: '1' }, { replace: true });
        } else if (isLocationRequiredError(err)) {
          setSearchParams({ step: '1' }, { replace: true });
          askLocation();
        } else {
          setSaveError(err.message);
        }
      } finally {
        setBusy(false);
      }
    },
    [blocker, flushSave, setSearchParams, navigate, runAdCheck, setStepParam, start, step, askLocation]
  );
  advanceRef.current = advance;

  const gateProceed = useCallback(() => {
    const target = gate.pendingTarget ?? undefined;
    setGate(g => ({ ...g, status: 'idle', pendingTarget: null }));
    void advanceRef.current(target, true);
  }, [gate.pendingTarget]);

  /** Ausweg bei dauerhaft fehlgeschlagener Prüfung: ohne Ergebnis weiter (zählt als Prüfung ohne Lücken). */
  const gateSkipCheck = useCallback(() => {
    // Ersatzergebnis sofort in den Zustand UND in den Ref schreiben: `advance` speichert aus dem Ref,
    // ein asynchrones `patch` käme zu spät und der alte Stand würde gespeichert.
    const withCheck = {
      ...stateRef.current,
      adCheck: withSignature(emptyCheckResult(), adSignatureSource(stateRef.current)),
    };
    stateRef.current = withCheck;
    setState(withCheck);
    setGate(g => ({ ...g, checkFailed: false, checkFailures: 0 }));
    void advanceRef.current(undefined, true);
  }, []);

  const gateAmend = useCallback(() => {
    setGate(g => ({
      ...g,
      status: 'idle',
      pendingTarget: null,
      highlight: true,
      focusTick: g.focusTick + 1,
    }));
  }, []);
  const next = useCallback(() => advance(), [advance]);

  const adoptCampaign = useCallback(
    (id: string, adopted: WizardState) => {
      setCampaignId(id);
      campaignIdRef.current = id;
      // Klon/Vorlage ohne gültige Prüfung öffnet Schritt 1, nicht Schritt 2.
      const target = clampStep(allowedStep(adopted.step, adopted.adCheck, adopted));
      const next: WizardState = { ...withWizardDefaults(adopted), step: target };
      setState(next);
      stateRef.current = next;
      // Ob die Kopie schon eine Suche hat, weiß nur der Server; bis dahin wird lieber gefragt.
      setHasSearch(undefined);
      searchConfirmed.current = null;
      void getWizard(id)
        .then(snap => {
          if (campaignIdRef.current === id) setHasSearch(snap.hasSearch);
        })
        .catch(() => undefined);
      navigate(
        {
          pathname: `${Routes.RECRUITING_BOOSTER_CO_PILOT}/${NEW_CAMPAIGN_ROUTE_SEGMENT}/${id}`,
          search: `?step=${target}`,
        },
        { replace: true }
      );
    },
    [navigate, setHasSearch]
  );

  const duplicateCampaign = useCallback(async () => {
    const source = stateRef.current;
    const sourceId = campaignIdRef.current;
    if (!sourceId) return;
    const suffix = i18n.t('wizDuplicateSuffix');
    // Name vergeben: (Kopie), (Kopie 2) … bis ein freier gefunden ist.
    for (let attempt = 1; ; attempt += 1) {
      try {
        const created = await createWizardCampaign({
          name: duplicateName(source.name, suffix, attempt),
          accountId: source.accountId,
          briefing: source.briefing,
          cloneFromCampaignId: sourceId,
          cloneContacts: false,
        });
        setLocked(false);
        adoptCampaign(created.campaignId, created.state);
        return;
      } catch (e) {
        if (!(e as WizardApiError).nameTaken || attempt >= 20) throw e;
      }
    }
  }, [adoptCampaign]);

  /* Prüfpflicht bei jedem Öffnen eines Schritts ≥ 2 (URL, Vorwärts, Laden) ------ */

  useEffect(() => {
    if (loading || running) return;
    const allowed = allowedStep(step, state.adCheck, state);
    if (allowed !== step) {
      setNotice(i18n.t('wizGateCheckRequired'));
      setSearchParams({ step: String(allowed) }, { replace: true });
    }
  }, [
    loading,
    running,
    step,
    state.adCheck,
    state.briefing,
    state.briefingExtras,
    setSearchParams,
  ]);

  /* Schätzung der Suchtreffer (Schritt 2) ----------------------------- */

  const estimateKey = JSON.stringify([
    state.method,
    state.locations,
    state.criteria,
  ]);
  useEffect(() => {
    if (!campaignId || step !== 2 || state.method !== 'saia' || state.locations.length === 0)
      return undefined;
    const controller = new AbortController();
    const timer = setTimeout(async () => {
      setEstimate(prev => ({ ...prev, status: 'loading' }));
      try {
        const res = await estimateSearch(
          campaignId,
          { locations: state.locations, criteria: state.criteria, method: state.method },
          controller.signal
        );
        setEstimate({
          value: res.estimate,
          status: res.status === 'ok' && res.estimate !== null ? 'ok' : 'unavailable',
          message: res.message,
        });
      } catch (e) {
        if (!controller.signal.aborted)
          setEstimate({
            value: null,
            status: 'unavailable',
            message: (e as WizardApiError).message,
          });
      }
    }, 900);
    return () => {
      clearTimeout(timer);
      controller.abort();
    };
  }, [campaignId, step, estimateKey]);

  /* Prüfung der eigenen Links (Schritt 2) --------------------------- */
  // Je Link einmal bei LinkedIn nachfragen (Ercan 21.10.2026: ein Projekt-Link ohne Suche fiel erst in Schritt 4 auf).
  // Das Ergebnis bleibt je Link gemerkt; ein neuer oder geänderter Link wird nach kurzer Pause geprüft.
  const [linkChecks, setLinkChecks] = useState<Record<string, EstimateState>>({});
  const linkChecksRef = useRef(linkChecks);
  linkChecksRef.current = linkChecks;
  const linksKey = JSON.stringify(
    state.method === 'url'
      ? searchLinkRows(state)
          .map(u => u.trim())
          .filter(isLinkedinUrl)
      : []
  );
  useEffect(() => {
    const links = JSON.parse(linksKey) as string[];
    if (!campaignId || step !== 2 || links.length === 0) return undefined;
    const todo = links.filter(u => !linkChecksRef.current[u]);
    if (todo.length === 0) return undefined;
    const controller = new AbortController();
    const timer = setTimeout(async () => {
      for (const url of todo) {
        if (controller.signal.aborted) return;
        setLinkChecks(prev => ({ ...prev, [url]: { value: null, status: 'loading' } }));
        try {
          const res = await estimateSearch(
            campaignId,
            { locations: [], criteria: stateRef.current.criteria, method: 'url', searchUrl: url },
            controller.signal
          );
          setLinkChecks(prev => ({
            ...prev,
            [url]: {
              value: res.estimate,
              status: res.status === 'ok' && res.estimate !== null ? 'ok' : 'unavailable',
              message: res.message,
              link: res.link,
            },
          }));
        } catch (e) {
          if (controller.signal.aborted) {
            // Abgebrochen (Link geändert): beim nächsten Mal neu prüfen.
            setLinkChecks(prev => {
              const next = { ...prev };
              delete next[url];
              return next;
            });
            return;
          }
          setLinkChecks(prev => ({
            ...prev,
            [url]: { value: null, status: 'unavailable', message: (e as WizardApiError).message },
          }));
        }
      }
    }, 900);
    return () => {
      clearTimeout(timer);
      controller.abort();
    };
  }, [campaignId, step, linksKey]);

  // Gesamtzahl der eigenen Links = Summe der funktionierenden mit Trefferzahl (wie im Backend; obere Grenze).
  useEffect(() => {
    if (state.method !== 'url') return;
    const counted = (JSON.parse(linksKey) as string[])
      .map(u => linkChecks[u])
      .filter((c): c is EstimateState => !!c && c.link === 'works' && c.value !== null);
    if (counted.length > 0)
      setEstimate({ value: counted.reduce((sum, c) => sum + (c.value ?? 0), 0), status: 'ok' });
  }, [linkChecks, linksKey, state.method]);

  /* Ablaufplan (Schritt 5–7) ----------------------------------------- */

  const answers = useMemo(() => flowAnswersFromState(state, account), [state, account]);
  const answersKey = JSON.stringify([answers, state.accountId]);
  useEffect(() => {
    if (!campaignId || step < 5 || step > 7) return undefined;
    const controller = new AbortController();
    // Sofort „wird berechnet“ zeigen (nicht erst nach der Entprellung), sonst blitzt kurz der Fehlertext auf.
    setFlowPlanLoading(true);
    setFlowPlanFailed(false);
    const timer = setTimeout(async () => {
      try {
        // Der Plan liest Konto & Co. aus dem gespeicherten Entwurf: erst speichern, dann planen.
        await flushSave().catch(() => undefined);
        if (controller.signal.aborted) return;
        const plan = await planFlow(campaignId, answers, controller.signal);
        setFlowPlan(plan);
      } catch {
        // Der Plan ist Anzeige; ein Fehler lässt den letzten Stand stehen.
        if (!controller.signal.aborted) setFlowPlanFailed(true);
      } finally {
        if (!controller.signal.aborted) setFlowPlanLoading(false);
      }
    }, 250);
    return () => {
      clearTimeout(timer);
      controller.abort();
    };
  }, [campaignId, step, answersKey]);

  const reload = useCallback(async () => {
    if (campaignIdRef.current) await load(campaignIdRef.current);
  }, [load]);

  // Laufansicht nach Reload: Stand holen.
  useEffect(() => {
    if (running && campaignId && !run) {
      void import('./campaign-wizard.api').then(({ getRun }) =>
        getRun(campaignId)
          .then(setRun)
          .catch(() => undefined)
      );
    }
  }, [running, campaignId, run]);

  return {
    campaignId,
    loading,
    loadError,
    locked,
    state,
    patch,
    step,
    goTo,
    gate,
    runAdCheck,
    gateProceed,
    gateSkipCheck,
    gateAmend,
    next,
    prev,
    cancel,
    openLegacySetup,
    saveDraft,
    start,
    blocker,
    showBlocker,
    busy,
    saving,
    saveError,
    // Der Hinweis zur Ortsfrage verschwindet, sobald die Prüfung veraltet ist oder die Ortsbox nicht mehr da ist.
    notice:
      notice !== null &&
      notice === i18n.t('wizGateLocationRequired') &&
      !locationQuestionVisible(state.adCheck, state)
        ? null
        : notice,
    nameTaken,
    startError,
    startErrorCode,
    warnings,
    flowError,
    searchMessage,
    searchFailure,
    setSearchFailure,
    accounts,
    accountsLoading,
    account,
    estimate,
    linkChecks,
    flowPlan,
    flowPlanLoading,
    flowPlanFailed,
    run,
    running,
    autoReplyDefaults: snapshot?.autoReplyDefaults ?? null,
    flushSave,
    reload,
    adoptCampaign,
    duplicateCampaign,
    searchRestartPending,
    confirmSearchRestart,
    cancelSearchRestart,
    searchRan: searchAlreadyRan(hasSearch, campaignId),
    markSearchExists,
  };
};
